import { createContext, ReactNode, useContext, useState, useCallback } from 'react';

import { TFileWithType } from '@treezor/design-system';

import { TUserCreationType } from '@/store/models/dashboard-config.model';
import { ITaxResidenceBase } from '@/store/models/taxResidence.model';

import { IPostUser as IBasePostUser, EParentType, IUser } from '@/features/user/store/user.model';

export interface ITaxResidenceCreate extends ITaxResidenceBase {
  isDirty?: boolean;
}
export interface ICreateUserInfo extends IBasePostUser {
  middleName1?: string;
  middleName2?: string;
  taxResidences?: ITaxResidenceCreate[];
  kycDocuments?: TFileWithType[];
  isSameAsPrincipalAddress?: 'yes' | 'no';
  isAttachedToParent?: 'yes' | 'no';
  roles?: `${EParentType}`[];
}

export interface IPostCreateUser extends ICreateUserInfo {
  members?: ICreateUserInfo[];
}

export interface ICreateUserContext {
  userType: TUserCreationType;
  isDirty: boolean;
  setIsDirty: (dirty: boolean) => void;
  selectUserType: (type: TUserCreationType) => void;
  user: IPostCreateUser;
  updateUser: (user: IPostCreateUser) => void;
  parentUser?: IUser;
  updateParentUser?: (user: IUser) => void;
  currentStep: number;
  setCurrentStep: (step: number) => void;
  nextStep: (max?: number) => void;
  prevStep: () => void;
  reset: () => void;
}

export const CreateUserContext = createContext<ICreateUserContext>({} as ICreateUserContext);

interface ICreateUserProvider {
  children: ReactNode;
}

export const CreateUserProvider = ({ children }: ICreateUserProvider) => {
  const [userType, setUserType] = useState<TUserCreationType>();
  const [user, setUserData] = useState<IPostCreateUser>();
  const [parentUser, setParentUser] = useState<IUser>();
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [isDirty, setIsDirty] = useState<boolean>(false);

  const nextStep = useCallback(
    (max?: number) => setCurrentStep((prev) => (max ? Math.min(max, prev + 1) : prev + 1)),
    [],
  );

  const prevStep = useCallback(
    () =>
      setCurrentStep((prev) => {
        if (prev === 0) setUserType(undefined);
        return Math.max(prev - 1, 0);
      }),
    [],
  );

  const reset = useCallback(() => {
    setUserData(undefined);
    setUserType(undefined);
    setCurrentStep(0);
    setIsDirty(false);
    setParentUser(undefined);
  }, []);

  return (
    <CreateUserContext.Provider
      value={{
        isDirty,
        setIsDirty,
        userType,
        selectUserType: setUserType,
        user,
        updateUser: setUserData,
        currentStep,
        setCurrentStep,
        nextStep,
        prevStep,
        reset,
        parentUser,
        updateParentUser: setParentUser,
      }}
    >
      {children}
    </CreateUserContext.Provider>
  );
};

export const useCreateUserContext = () => useContext(CreateUserContext);
